'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createClient } from '../../lib/supabase/client';
import LandingFooter from '../landing-page/components/LandingFooter';
import SiteNav from '@/components/SiteNav';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ModuleInfo {
  id: number;
  number: string;
  title: string;
  description: string;
  lessonIds: string[];
  status: 'available' | 'locked' | 'coming-soon';
  href: string;
}

// ─── Module data ──────────────────────────────────────────────────────────────

const MODULES: ModuleInfo[] = [
  {
    id: 1,
    number: '01',
    title: 'Copilot at Work',
    description: 'What it is, where to find it, and how to use it from day one.',
    lessonIds: ['lesson-1-1', 'lesson-1-2', 'lesson-1-3', 'lesson-1-4', 'lesson-1-5', 'lesson-1-6'],
    status: 'available',
    href: '/module/1',
  },
  {
    id: 2,
    number: '02',
    title: 'Choosing Your AI',
    description: 'Which AI tools you actually need and which subscription is worth paying for.',
    lessonIds: ['lesson-2-1', 'lesson-2-2', 'lesson-2-3', 'lesson-2-4', 'lesson-2-5'],
    status: 'available',
    href: '/module/2',
  },
  {
    id: 3,
    number: '03',
    title: 'Communication at Work',
    description: 'Emails, reports, difficult conversations. Use AI to write faster, sound sharper, and never stare at a blank page again.',
    lessonIds: ['lesson-3-1', 'lesson-3-2', 'lesson-3-3', 'lesson-3-4', 'lesson-3-5'],
    status: 'available',
    href: '/module/3',
  },
  {
    id: 4,
    number: '04',
    title: 'Staying Relevant',
    description: 'How to use AI to hold your own in any room — including workplaces changing quickly around you.',
    lessonIds: ['lesson-4-1', 'lesson-4-2', 'lesson-4-3', 'lesson-4-4', 'lesson-4-5'],
    status: 'available',
    href: '/module/4',
  },
  {
    id: 5,
    number: '05',
    title: 'Working Smarter',
    description: 'Research, business cases, presentations. AI as your thinking partner.',
    lessonIds: ['lesson-5-1', 'lesson-5-2', 'lesson-5-3', 'lesson-5-4', 'lesson-5-5'],
    status: 'available',
    href: '/module/5',
  },
  {
    id: 6,
    number: '06',
    title: 'Everyday Life Admin',
    description: 'The letters, appointments, and admin that pile up.',
    lessonIds: ['lesson-6-1', 'lesson-6-2', 'lesson-6-3', 'lesson-6-4'],
    status: 'available',
    href: '/module/6',
  },
  {
    id: 7,
    number: '07',
    title: 'Travel and New Experiences',
    description: 'Plan trips, research moves, learn new things.',
    lessonIds: ['lesson-7-1', 'lesson-7-2', 'lesson-7-3', 'lesson-7-4'],
    status: 'available',
    href: '/module/7',
  },
  {
    id: 8,
    number: '08',
    title: 'Career Protection',
    description: 'Use AI to update your CV, research your market value, and navigate what comes next — on your terms.',
    lessonIds: ['lesson-8-1', 'lesson-8-2', 'lesson-8-3', 'lesson-8-4'],
    status: 'available',
    href: '/module/8',
  },
  {
    id: 9,
    number: '09',
    title: 'Your Next Chapter',
    description: 'What life looks like after this role. Use AI to explore options, build a plan, and make the next chapter deliberate.',
    lessonIds: ['lesson-9-1', 'lesson-9-2', 'lesson-9-3', 'lesson-9-4'],
    status: 'available',
    href: '/module/9',
  },
];

const MODULE_IDS_FOR_VAULT = ['module-1','module-2','module-3','module-4','module-5','module-6','module-7','module-8','module-9'];

// ─── Tab type ─────────────────────────────────────────────────────────────────

type Tab = 'progress' | 'certificates' | 'account';

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function DashboardPage() {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());
  const [completedModuleIds, setCompletedModuleIds] = useState<Set<string>>(new Set());
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('progress');
  const [showWelcomeCard, setShowWelcomeCard] = useState(false);

  // Account settings state
  const [displayName, setDisplayName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [saveNameMsg, setSaveNameMsg] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) {
        router.push('/landing-page');
        return;
      }

      // Require email confirmation before accessing paid content
      if (!session.user.email_confirmed_at) {
        setEmailUnverified(true);
        setLoading(false);
        return;
      }

      setUser(session.user);

      // Load profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();
      if (profile) {
        setUserProfile(profile);
        setDisplayName(profile.full_name || '');
        // Show welcome card only if not yet dismissed
        setShowWelcomeCard(!profile.welcome_card_dismissed);
      } else {
        setDisplayName(session.user.email?.split('@')[0] || '');
        // New user — show welcome card
        setShowWelcomeCard(true);
      }

      // Load lesson progress
      const { data: progressData } = await supabase
        .from('lesson_progress')
        .select('lesson_id, module_id')
        .eq('user_id', session.user.id);

      if (progressData) {
        const lessonSet = new Set(progressData.map((r: { lesson_id: string }) => r.lesson_id));
        const modSet = new Set(progressData.map((r: { module_id: string }) => r.module_id));
        setCompletedLessons(lessonSet);
        setCompletedModuleIds(modSet);
        const count = MODULE_IDS_FOR_VAULT.filter((mid) => modSet.has(mid)).length;
        setVaultUnlocked(count >= 9);
      }

      setLoading(false);
    });
  }, [router]);

  const handleSaveName = async () => {
    if (!user || !displayName.trim()) return;
    setSavingName(true);
    setSaveNameMsg('');
    const supabase = createClient();
    const { error } = await supabase
      .from('user_profiles')
      .upsert({ id: user.id, email: user.email, full_name: displayName.trim() });
    setSavingName(false);
    setSaveNameMsg(error ? 'Could not save. Please try again.' : 'Name updated.');
    setTimeout(() => setSaveNameMsg(''), 3000);
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setChangingPassword(true);
    setPasswordMsg('');
    const supabase = createClient();
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/dashboard`,
    });
    setChangingPassword(false);
    setPasswordMsg(error ? 'Could not send reset email.' : 'Password reset email sent — check your inbox.');
    setTimeout(() => setPasswordMsg(''), 5000);
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    const supabase = createClient();
    await supabase.auth.signOut();
    router.push('/landing-page');
  };

  const handleDismissWelcomeCard = async () => {
    setShowWelcomeCard(false);
    if (!user) return;
    const supabase = createClient();
    await supabase
      .from('user_profiles')
      .upsert({ id: user.id, email: user.email, welcome_card_dismissed: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--amber)', borderTopColor: 'transparent' }} />
          <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  if (emailUnverified) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: '#1B2A4A' }}>
        <div className="w-full max-w-md card-warm p-8 sm:p-10 text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: '#FEF9EC' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <h1 className="font-display text-2xl font-semibold mb-3" style={{ color: 'var(--navy)' }}>
            Please verify your email
          </h1>
          <p className="font-body text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
            We sent a confirmation link to your inbox. Click it to activate your account and access your dashboard.
          </p>
          <p className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
            Didn&apos;t receive it? Check your spam folder, or{' '}
            <a href="/signup" className="underline hover:opacity-80" style={{ color: 'var(--amber)' }}>
              sign up again
            </a>{' '}
            with the same email.
          </p>
        </div>
      </div>
    );
  }

  const totalModulesCompleted = MODULES.filter((m) => completedModuleIds.has(`module-${m.id}`)).length;
  const overallLessons = MODULES.reduce((acc, m) => acc + m.lessonIds.length, 0);
  const completedLessonsCount = MODULES.reduce(
    (acc, m) => acc + m.lessonIds.filter((lid) => completedLessons.has(lid)).length,
    0
  );
  const overallPercent = overallLessons > 0 ? Math.round((completedLessonsCount / overallLessons) * 100) : 0;
  const firstName = (userProfile?.full_name || user?.email || '').split(' ')[0] || 'there';
  const allModulesComplete = totalModulesCompleted === 9;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      <SiteNav variant="white" currentPath="/dashboard" />

      {/* ── HERO HEADER ──────────────────────────────────────────────────── */}
      <section style={{ backgroundColor: 'var(--navy)' }}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 xl:px-20 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <span
                className="font-body text-xs font-semibold tracking-widest uppercase mb-3 block"
                style={{ color: 'var(--amber)' }}
              >
                My Dashboard
              </span>
              <h1 className="font-display text-4xl md:text-5xl font-semibold text-white mb-3 leading-tight">
                Welcome back, {firstName}.
              </h1>
              <p className="font-body text-base" style={{ color: 'rgba(255,255,255,0.65)' }}>
                Everything is here when you need it. Work through the modules in order, or jump to what you need most. Complete all nine and the Deep Dive Vault unlocks — our highest-stakes prompts for the situations that matter most.
              </p>
            </div>

            {/* Overall progress pill */}
            <div
              className="md:w-64 rounded-2xl p-5 flex flex-col gap-3 flex-shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-white">Overall progress</span>
                <span className="font-body text-sm font-bold" style={{ color: 'var(--amber)' }}>
                  {overallPercent}%
                </span>
              </div>
              <div className="h-2 rounded-full w-full" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                <div
                  className="h-2 rounded-full transition-all duration-700"
                  style={{
                    width: `${overallPercent}%`,
                    backgroundColor: 'var(--amber)',
                  }}
                />
              </div>
              <p className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {completedLessonsCount} of {overallLessons} lessons complete
              </p>
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex gap-1 mt-10 border-b" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
            {([
              { key: 'progress', label: 'Module Progress' },
              { key: 'certificates', label: 'Certificates & Downloads' },
              { key: 'account', label: 'Account Settings' },
            ] as { key: Tab; label: string }[]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="font-body text-sm font-medium px-5 py-3 transition-all duration-200 border-b-2 -mb-px"
                style={{
                  color: activeTab === tab.key ? 'var(--amber)' : 'rgba(255,255,255,0.55)',
                  borderBottomColor: activeTab === tab.key ? 'var(--amber)' : 'transparent',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── TAB CONTENT ──────────────────────────────────────────────────── */}
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 xl:px-20 py-14 lg:py-20">

        {/* ── FIRST LOGIN WELCOME CARD ─────────────────────────────────── */}
        {showWelcomeCard && activeTab === 'progress' && (
          <div
            className="w-full rounded-2xl p-8 lg:p-10 mb-10"
            style={{ backgroundColor: '#FDF6EC', border: '1px solid rgba(212,137,26,0.25)' }}
          >
            <p
              className="font-body text-xs font-semibold uppercase tracking-widest mb-4"
              style={{ color: '#D4891A', letterSpacing: '0.14em' }}
            >
              YOU&rsquo;RE IN THE RIGHT PLACE
            </p>
            <h2
              className="font-display font-semibold text-2xl lg:text-3xl mb-5"
              style={{ color: '#1B2A4A' }}
            >
              Welcome. Let&rsquo;s start simply.
            </h2>
            <div className="space-y-5 font-body text-base max-w-2xl" style={{ color: '#374151', lineHeight: '1.85' }}>
              <p>
                Everything in here is in plain English. Nothing assumes you&rsquo;re technical. Nothing is going to make you feel like you should already know this.
              </p>
              <p>
                Start with the four short orientation lessons — they take about twenty minutes. By the end of them you&rsquo;ll understand AI clearly and you&rsquo;ll have tried something yourself.
              </p>
              <p>Then the main course begins.</p>
              <p className="font-medium italic" style={{ color: '#1B2A4A' }}>— Anna</p>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <a
                href="/orientation"
                className="inline-flex items-center justify-center font-body text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 active:scale-95"
                style={{ backgroundColor: '#D4891A', color: 'white' }}
              >
                Start Orientation →
              </a>
              <button
                onClick={handleDismissWelcomeCard}
                className="font-body text-sm transition-colors duration-150 hover:opacity-70 text-left sm:text-right sm:ml-auto"
                style={{ color: '#9CA3AF' }}
              >
                Got it — take me to my dashboard →
              </button>
            </div>
          </div>
        )}

        {/* ── PROGRESS TAB ─────────────────────────────────────────────── */}
        {activeTab === 'progress' && (
          <div className="space-y-5">
            <div className="mb-10">
              <h2 className="font-display text-3xl font-semibold mb-1" style={{ color: 'var(--navy)' }}>
                Lesson Progress
              </h2>
              <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>
                Each module shows how many lessons you've completed. Click to continue or review.
              </p>
            </div>

            {/* ── ANNA'S LATEST CARD ──────────────────────────────────── */}
            <div
              className="w-full rounded-xl p-6 lg:p-8 mb-6"
              style={{
                backgroundColor: '#FDF6EC',
                borderLeft: '3px solid #D4891A',
                border: '1px solid rgba(212,137,26,0.2)',
                borderLeftWidth: '3px',
                borderLeftColor: '#D4891A',
              }}
            >
              <p
                className="font-body text-xs font-semibold uppercase tracking-widest mb-1"
                style={{ color: '#D4891A', letterSpacing: '0.14em' }}
              >
                ANNA&rsquo;S LATEST
              </p>
              <p className="font-body text-xs mb-3" style={{ color: '#9CA3AF' }}>
                May 2026
              </p>
              <h3
                className="font-display text-xl font-semibold mb-2 leading-snug"
                style={{ color: '#1B2A4A' }}
              >
                Everyone Is Talking About AI Agents. Here&rsquo;s What Actually Matters.
              </h3>
              <p className="font-body text-sm leading-relaxed mb-4" style={{ color: '#1B2A4A' }}>
                AI agents are getting a lot of coverage right now. Most of it can safely be ignored. Here&rsquo;s the part that&rsquo;s actually relevant to you.
              </p>
              <Link
                href="/updates"
                className="font-body text-sm font-semibold transition-colors duration-150 hover:opacity-75"
                style={{ color: '#D4891A' }}
              >
                Read this update →
              </Link>
            </div>

            {/* ── POST-COMPLETION STATE ────────────────────────────────── */}
            {allModulesComplete && (
              <div
                className="w-full rounded-2xl px-8 py-10 lg:px-12 lg:py-14 mb-6 text-center"
                style={{ backgroundColor: '#1B2A4A' }}
              >
                <p
                  className="font-body text-xs font-semibold uppercase tracking-widest mb-4"
                  style={{ color: '#D4891A', letterSpacing: '0.14em' }}
                >
                  YOUR FOUNDATION IS BUILT
                </p>
                <h2
                  className="font-display text-3xl lg:text-4xl font-semibold text-white mb-5 leading-tight"
                >
                  Nine modules. You did the work.
                </h2>
                <p
                  className="font-body text-base leading-relaxed mx-auto mb-8"
                  style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '560px' }}
                >
                  You&rsquo;ve built the foundation. That&rsquo;s not a small thing — most people who start something like this don&rsquo;t finish it.
                  <br /><br />
                  The Deep Dive Vault is waiting. It&rsquo;s the higher-stakes material: redundancy negotiations, board presentations, building something of your own with AI. The prompts that needed everything else first.
                  <br /><br />
                  And this dashboard stays yours. Anna checks in when something worth your attention happens.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                  <Link
                    href="/the-harder-stuff"
                    className="inline-flex items-center justify-center font-body text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 active:scale-95"
                    style={{ backgroundColor: '#D4891A', color: 'white' }}
                  >
                    Enter the Deep Dive Vault →
                  </Link>
                  <Link
                    href="/bookshelf"
                    className="inline-flex items-center justify-center font-body text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 active:scale-95"
                    style={{ backgroundColor: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.4)' }}
                  >
                    Browse Anna&rsquo;s Bookshelf →
                  </Link>
                </div>
                <p
                  className="font-body text-sm italic"
                  style={{ color: '#FDF6EC' }}
                >
                  Well done. Genuinely. — Anna
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {MODULES.map((mod) => {
                const modKey = `module-${mod.id}`;
                const isModuleComplete = completedModuleIds.has(modKey);
                const lessonsCompleted = mod.lessonIds.filter((lid) => completedLessons.has(lid)).length;
                const total = mod.lessonIds.length;
                const pct = total > 0 ? Math.round((lessonsCompleted / total) * 100) : 0;
                const isComingSoon = mod.status === 'coming-soon';
                const hasStarted = lessonsCompleted > 0;

                return (
                  <div
                    key={mod.id}
                    className={`bg-white rounded-xl p-7 flex flex-col gap-4 transition-all duration-200 hover:shadow-md`}
                    style={{
                      border: isModuleComplete
                        ? '2px solid #16a34a'
                        : hasStarted
                        ? '2px solid var(--amber)'
                        : '1px solid var(--border)',
                    }}
                  >
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-3">
                      <span
                        className="font-display text-4xl font-light leading-none"
                        style={{ color: 'var(--amber)' }}
                      >
                        {mod.number}
                      </span>
                      {isModuleComplete ? (
                        <span className="font-body text-xs font-semibold tracking-wider px-2.5 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 flex items-center gap-1">
                          ✓ COMPLETE
                        </span>
                      ) : hasStarted ? (
                        <span className="font-body text-xs font-semibold tracking-wider px-2.5 py-1 rounded-full" style={{ backgroundColor: 'var(--amber-pale)', color: 'var(--amber)', border: '1px solid rgba(212,137,26,0.3)' }}>
                          IN PROGRESS
                        </span>
                      ) : (
                        <span className="font-body text-xs font-semibold tracking-wider px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                          NOT STARTED
                        </span>
                      )}
                    </div>

                    {/* Title + description */}
                    <div>
                      <h3 className="font-display text-xl font-semibold mb-1" style={{ color: 'var(--navy)' }}>
                        Module {mod.number} — {mod.title}
                      </h3>
                      <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                        {mod.description}
                      </p>
                    </div>

                    {/* Progress bar */}
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
                          {lessonsCompleted} of {total} lessons
                        </span>
                        <span className="font-body text-xs font-semibold" style={{ color: isModuleComplete ? '#16a34a' : 'var(--amber)' }}>
                          {pct}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full w-full" style={{ backgroundColor: 'var(--silver-light)' }}>
                        <div
                          className="h-1.5 rounded-full transition-all duration-500"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: isModuleComplete ? '#16a34a' : 'var(--amber)',
                          }}
                        />
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="mt-auto">
                      <Link
                          href={mod.href}
                          className="inline-block font-body text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 active:scale-95"
                          style={{ backgroundColor: isModuleComplete ? 'rgba(22,163,74,0.1)' : 'var(--navy)', color: isModuleComplete ? '#16a34a' : 'white' }}
                        >
                          {isModuleComplete ? 'Review Module →' : hasStarted ? 'Continue →' : 'Start Module →'}
                        </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Vault card */}
            <div
              className="rounded-xl p-7 flex flex-col sm:flex-row sm:items-center gap-5 mt-2"
              style={{ backgroundColor: '#1a2540', border: '1px solid rgba(212,137,26,0.25)' }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="font-body text-xs font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: 'rgba(212,137,26,0.2)', color: 'var(--amber)', border: '1px solid rgba(212,137,26,0.3)' }}
                  >
                    THE HARDER STUFF
                  </span>
                  {!vaultUnlocked && <span aria-hidden="true">🔒</span>}
                </div>
                <h3 className="font-display text-xl font-semibold mb-1 text-white">
                  {vaultUnlocked ? 'The Harder Stuff — Unlocked' : 'The Harder Stuff'}
                </h3>
                <p className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  {vaultUnlocked
                    ? "You finished. Here's what we kept back."
                    : `Complete all 9 modules and this unlocks. It's worth it. ${totalModulesCompleted} of 9 done.`}
                </p>
              </div>
              <div className="flex-shrink-0">
                {vaultUnlocked ? (
                  <Link
                    href="/the-harder-stuff"
                    className="inline-flex items-center gap-2 font-body text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 active:scale-95"
                    style={{ backgroundColor: 'var(--amber)', color: 'white' }}
                  >
                    Enter the Vault →
                  </Link>
                ) : (
                  <button
                    disabled
                    className="inline-flex items-center gap-2 font-body text-sm font-semibold px-5 py-2.5 rounded-lg cursor-not-allowed"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)' }}
                  >
                    Keep going →
                  </button>
                )}
              </div>
            </div>

            {/* Anna's Bookshelf card */}
            <div
              className="rounded-xl p-7 flex flex-col sm:flex-row sm:items-center gap-5 mt-2"
              style={{ backgroundColor: 'white', border: '1px solid var(--border)' }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl" aria-hidden="true">📚</span>
                </div>
                <h3 className="font-display text-xl font-semibold mb-1" style={{ color: 'var(--navy)' }}>
                  Anna&rsquo;s Bookshelf
                </h3>
                <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>
                  24 books and the tools Anna actually uses.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Link
                  href="/bookshelf"
                  className="inline-flex items-center gap-2 font-body text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 active:scale-95"
                  style={{ backgroundColor: 'var(--navy)', color: 'white' }}
                >
                  Browse the Bookshelf →
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* ── CERTIFICATES & DOWNLOADS TAB ─────────────────────────────── */}
        {activeTab === 'certificates' && (
          <div>
            <div className="mb-10">
              <h2 className="font-display text-3xl font-semibold mb-1" style={{ color: 'var(--navy)' }}>
                Certificates &amp; Downloads
              </h2>
              <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>
                Complete modules to unlock your certificate and downloadable resources.
              </p>
            </div>

            {/* Certificate of Completion */}
            <div
              className="bg-white rounded-2xl p-8 mb-5 flex flex-col sm:flex-row sm:items-center gap-6"
              style={{
                backgroundColor: totalModulesCompleted === 9 ? 'var(--navy)' : 'white',
                border: totalModulesCompleted === 9 ? '1px solid rgba(212,137,26,0.3)' : '1px solid var(--border)',
              }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: totalModulesCompleted === 9 ? 'rgba(212,137,26,0.2)' : 'var(--silver-light)',
                  border: totalModulesCompleted === 9 ? '2px solid rgba(212,137,26,0.4)' : '2px solid var(--border)',
                }}
              >
                <span className="text-3xl" aria-hidden="true">{totalModulesCompleted === 9 ? '🏆' : '🎓'}</span>
              </div>
              <div className="flex-1">
                <h3
                  className="font-display text-2xl font-semibold mb-1"
                  style={{ color: totalModulesCompleted === 9 ? 'white' : 'var(--navy)' }}
                >
                  Certificate of Completion
                </h3>
                <p
                  className="font-body text-sm leading-relaxed"
                  style={{ color: totalModulesCompleted === 9 ? 'rgba(255,255,255,0.65)' : 'var(--text-muted)' }}
                >
                  {totalModulesCompleted === 9
                    ? "You've completed all nine modules of That Clicked. Your certificate is ready."
                    : `Complete all 9 modules to earn your certificate. ${totalModulesCompleted} of 9 done.`}
                </p>
                {totalModulesCompleted < 9 && (
                  <div className="mt-3">
                    <div className="h-1.5 rounded-full w-full max-w-xs" style={{ backgroundColor: 'var(--silver-light)' }}>
                      <div
                        className="h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${(totalModulesCompleted / 9) * 100}%`, backgroundColor: 'var(--amber)' }}
                      />
                    </div>
                    <p className="font-body text-xs mt-1.5" style={{ color: 'var(--text-light)' }}>
                      {9 - totalModulesCompleted} module{9 - totalModulesCompleted !== 1 ? 's' : ''} remaining
                    </p>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                {totalModulesCompleted === 9 ? (
                  <Link
                    href="/certificate"
                    className="inline-flex items-center gap-2 font-body text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 active:scale-95"
                    style={{ backgroundColor: 'var(--amber)', color: 'white' }}
                  >
                    View &amp; Download →
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center gap-2 font-body text-xs font-semibold px-4 py-2 rounded-lg"
                    style={{ backgroundColor: 'var(--silver-light)', color: 'var(--text-muted)' }}
                  >
                    🔒 Locked
                  </span>
                )}
              </div>
            </div>

            {/* Completion Badges */}
            <div className="mb-8">
              <h3 className="font-display text-2xl font-semibold mb-1" style={{ color: 'var(--navy)' }}>
                Module Badges
              </h3>
              <p className="font-body text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                Earn a badge for each module you complete.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-4">
                {MODULES.map((mod) => {
                  const earned = completedModuleIds.has(`module-${mod.id}`);
                  return (
                    <div key={mod.id} className="flex flex-col items-center gap-2">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300"
                        style={{
                          backgroundColor: earned ? 'rgba(212,137,26,0.15)' : 'var(--silver-light)',
                          border: earned ? '2px solid var(--amber)' : '2px solid var(--border)',
                        }}
                        title={earned ? `Module ${mod.number} — ${mod.title}` : `Complete Module ${mod.number} to earn`}
                      >
                        {earned ? (
                          <span className="text-xl" aria-hidden="true">⭐</span>
                        ) : (
                          <span className="font-display text-lg font-semibold" style={{ color: 'var(--silver)' }}>
                            {mod.number}
                          </span>
                        )}
                      </div>
                      <span
                        className="font-body text-xs text-center leading-tight"
                        style={{ color: earned ? 'var(--amber)' : 'var(--text-light)', maxWidth: '60px' }}
                      >
                        {earned ? mod.title : `M${mod.id}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Downloads */}
            <div>
              <h3 className="font-display text-2xl font-semibold mb-1" style={{ color: 'var(--navy)' }}>
                Downloads
              </h3>
              <p className="font-body text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                Resources you can keep. Unlock more as you complete modules.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Copilot Cheat Sheet — always available */}
                <div
                  className="bg-white rounded-xl p-7 flex flex-col gap-4"
                  style={{ border: '2px solid var(--amber)' }}
                >
                  <div>
                    <h4 className="font-display text-lg font-semibold mb-1" style={{ color: 'var(--navy)' }}>
                      AI Cheat Sheet
                    </h4>
                    <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                      30 prompts for Outlook, Teams, Word, Excel, PowerPoint, and more.
                    </p>
                  </div>
                  <a
                    href="/assets/images/ThatClicked_AICheatSheet.pdf"
                    download="ThatClicked_AICheatSheet.pdf"
                    className="mt-auto font-body text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 active:scale-95 w-fit inline-block text-center"
                    style={{ backgroundColor: 'var(--amber)', color: 'white' }}
                  >
                    Download AI Cheat Sheet
                  </a>
                </div>

                {/* Module PDFs — locked until module complete */}
                {MODULES.slice(0, 5).map((mod) => {
                  const unlocked = completedModuleIds.has(`module-${mod.id}`);
                  return (
                    <div
                      key={mod.id}
                      className={`bg-white rounded-xl p-7 flex flex-col gap-4 ${!unlocked ? 'opacity-60' : ''}`}
                      style={{ border: `1px solid ${unlocked ? 'var(--amber)' : 'var(--border)'}` }}
                    >
                      <div>
                        <h4
                          className="font-display text-lg font-semibold mb-1"
                          style={{ color: unlocked ? 'var(--navy)' : 'var(--text-muted)' }}
                        >
                          Module {mod.number} — Full Content PDF
                        </h4>
                        <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                          Download the complete Module {mod.number} as a PDF to keep and reference anytime.
                        </p>
                      </div>
                      <div className="mt-auto">
                        {unlocked ? (
                          <button
                            className="font-body text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 active:scale-95"
                            style={{ backgroundColor: 'var(--amber)', color: 'white' }}
                          >
                            Download PDF
                          </button>
                        ) : (
                          <span className="font-body text-xs italic" style={{ color: 'var(--text-light)' }}>
                            🔒 Complete Module {mod.number} to unlock
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── ACCOUNT SETTINGS TAB ─────────────────────────────────────── */}
        {activeTab === 'account' && (
          <div className="max-w-2xl">
            <div className="mb-10">
              <h2 className="font-display text-3xl font-semibold mb-1" style={{ color: 'var(--navy)' }}>
                Account Settings
              </h2>
              <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>
                Manage your name, password, and account preferences.
              </p>
            </div>

            {/* Account info card */}
            <div className="bg-white rounded-2xl p-8 mb-5" style={{ border: '1px solid var(--border)' }}>
              <h3 className="font-display text-xl font-semibold mb-6" style={{ color: 'var(--navy)' }}>
                Your Details
              </h3>

              {/* Email (read-only) */}
              <div className="mb-6">
                <label className="font-body text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: 'var(--text-muted)' }}>
                  Email address
                </label>
                <div
                  className="font-body text-sm px-4 py-3 rounded-lg"
                  style={{ backgroundColor: 'var(--background)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                >
                  {user?.email}
                </div>
                <p className="font-body text-xs mt-1.5 italic" style={{ color: 'var(--text-light)' }}>
                  Email cannot be changed.
                </p>
              </div>

              {/* Display name */}
              <div className="mb-6">
                <label className="font-body text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: 'var(--text-muted)' }}>
                  Display name
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name"
                    className="flex-1 font-body text-sm px-4 py-3 rounded-lg outline-none transition-all duration-200 focus:ring-2"
                    style={{
                      border: '1px solid var(--border)',
                      color: 'var(--text-primary)',
                      backgroundColor: 'white',
                    }}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={savingName}
                    className="font-body text-sm font-semibold px-5 py-3 rounded-lg transition-all duration-200 active:scale-95 disabled:opacity-60"
                    style={{ backgroundColor: 'var(--navy)', color: 'white' }}
                  >
                    {savingName ? 'Saving…' : 'Save'}
                  </button>
                </div>
                {saveNameMsg && (
                  <p
                    className="font-body text-xs mt-2"
                    style={{ color: saveNameMsg.includes('Could not') ? '#dc2626' : '#16a34a' }}
                  >
                    {saveNameMsg}
                  </p>
                )}
              </div>

              {/* Member since */}
              <div>
                <label className="font-body text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: 'var(--text-muted)' }}>
                  Member since
                </label>
                <div
                  className="font-body text-sm px-4 py-3 rounded-lg"
                  style={{ backgroundColor: 'var(--background)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                >
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                    : '—'}
                </div>
              </div>
            </div>

            {/* Password card */}
            <div className="bg-white rounded-2xl p-8 mb-5" style={{ border: '1px solid var(--border)' }}>
              <h3 className="font-display text-xl font-semibold mb-2" style={{ color: 'var(--navy)' }}>
                Password
              </h3>
              <p className="font-body text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                We'll send a password reset link to your email address.
              </p>
              <button
                onClick={handleResetPassword}
                disabled={changingPassword}
                className="font-body text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 active:scale-95 disabled:opacity-60"
                style={{ backgroundColor: 'var(--navy)', color: 'white' }}
              >
                {changingPassword ? 'Sending…' : 'Send password reset email'}
              </button>
              {passwordMsg && (
                <p
                  className="font-body text-xs mt-3"
                  style={{ color: passwordMsg.includes('Could not') ? '#dc2626' : '#16a34a' }}
                >
                  {passwordMsg}
                </p>
              )}
            </div>

            {/* Progress summary card */}
            <div className="bg-white rounded-2xl p-8 mb-5" style={{ border: '1px solid var(--border)' }}>
              <h3 className="font-display text-xl font-semibold mb-4" style={{ color: 'var(--navy)' }}>
                Your Progress Summary
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="font-display text-4xl font-semibold mb-1" style={{ color: 'var(--amber)' }}>
                    {totalModulesCompleted}
                  </div>
                  <div className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>Modules complete</div>
                </div>
                <div className="text-center">
                  <div className="font-display text-4xl font-semibold mb-1" style={{ color: 'var(--amber)' }}>
                    {completedLessonsCount}
                  </div>
                  <div className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>Lessons done</div>
                </div>
                <div className="text-center">
                  <div className="font-display text-4xl font-semibold mb-1" style={{ color: 'var(--amber)' }}>
                    {overallPercent}%
                  </div>
                  <div className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>Overall progress</div>
                </div>
              </div>
            </div>

            {/* Sign out */}
            <div className="bg-white rounded-2xl p-8" style={{ border: '1px solid var(--border)' }}>
              <h3 className="font-display text-xl font-semibold mb-2" style={{ color: 'var(--navy)' }}>
                Sign out
              </h3>
              <p className="font-body text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                You can sign back in at any time — your progress is always saved.
              </p>
              <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="font-body text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 active:scale-95 disabled:opacity-60"
                style={{ backgroundColor: 'rgba(220,38,38,0.08)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.2)' }}
              >
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          </div>
        )}
      </div>

      <LandingFooter />
    </div>
  );
}
